import { supabase, handleSupabaseError } from "./supabase";

// Generate UUID for session-based KYC applications
const generateSessionId = () => {
  return "kyc_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
};

// KYC Service - Simplified for direct submission (No intermediate saves)
export class KYCService {
  // Create and submit KYC application in one operation

  static async checkSupabaseConnection() {
    const { data, error } = await supabase.from("kyc_applications").select("*");
    if (error) {
      console.log("Error checking supabase conection:", error);
      return handleSupabaseError(error);
    }
    return { success: true, data };
  }
  static async createKYCApplication(formData) {
    try {
      // Generate unique session ID for this application
      const sessionId = generateSessionId();

      // Basic email validation only (no verification required)
      if (formData.personalInfo?.email) {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!emailRegex.test(formData.personalInfo.email)) {
          return { error: "Valid email format required" };
        }
      }

      // Prepare the data for insertion
      const kycRecord = {
        session_id: sessionId,
        user_email: formData.personalInfo?.email || null,
        personal_info: formData.personalInfo || {},
        address_info: formData.address || {},
        employment_info: formData.employment || {},
        financial_info: formData.financial || {},
        business_info: formData.business || {},
        identity_info: formData.identity || {},
        disclosures_info: formData.disclosures || {},
        risk_profile: formData.riskProfile || "low",
        status: "submitted", // Direct submission - no draft status
        current_step: formData.currentStep || 8,
        completed_steps: formData.completedSteps || [],
        submitted_at: new Date().toISOString(),
        metadata: {
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          submitted_at: new Date().toISOString(),
          session_id: sessionId,
          ip_address: formData.ipAddress || null,
          user_agent: formData.userAgent || navigator?.userAgent || null,
        },
      };

      const { data, error } = await supabase
        .from("kyc_applications")
        .insert([kycRecord])
        .select()
        .single();

      if (error) {
        console.log("Error creating KYC application:", error);
        return handleSupabaseError(error);
      }

      return {
        success: true,
        data: data,
        sessionId: sessionId,
      };
    } catch (error) {
      console.log("Error creating KYC application:", error);
      return handleSupabaseError(error);
    }
  }

  // Get KYC application by ID (for admin/review purposes)
  static async getKYCApplication(applicationId) {
    try {
      const { data, error } = await supabase
        .from("kyc_applications")
        .select("*")
        .eq("id", applicationId)
        .single();

      if (error) {
        return handleSupabaseError(error);
      }

      return { success: true, data };
    } catch (error) {
      return handleSupabaseError(error);
    }
  }

  // Get all KYC applications (for admin purposes)
  static async getAllKYCApplications() {
    try {
      const { data, error } = await supabase
        .from("kyc_applications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        return handleSupabaseError(error);
      }

      return { success: true, data };
    } catch (error) {
      return handleSupabaseError(error);
    }
  }

  // Get KYC applications by status (for admin filtering)
  static async getKYCApplicationsByStatus(status) {
    try {
      const { data, error } = await supabase
        .from("kyc_applications")
        .select("*")
        .eq("status", status)
        .order("created_at", { ascending: false });

      if (error) {
        return handleSupabaseError(error);
      }

      return { success: true, data };
    } catch (error) {
      return handleSupabaseError(error);
    }
  }

  // Update application status (admin only)
  static async updateApplicationStatus(
    applicationId,
    newStatus,
    adminNotes = null
  ) {
    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };

      // Add status-specific timestamps
      if (newStatus === "approved") {
        updateData.approved_at = new Date().toISOString();
      } else if (newStatus === "rejected") {
        updateData.rejected_at = new Date().toISOString();
      }

      // Add admin notes if provided
      if (adminNotes) {
        updateData.metadata = {
          admin_notes: adminNotes,
          status_updated_at: new Date().toISOString(),
        };
      }

      const { data, error } = await supabase
        .from("kyc_applications")
        .update(updateData)
        .eq("id", applicationId)
        .select()
        .single();

      if (error) {
        return handleSupabaseError(error);
      }

      return { success: true, data };
    } catch (error) {
      return handleSupabaseError(error);
    }
  }

  // Delete application (admin only)
  static async deleteKYCApplication(applicationId) {
    try {
      const { error } = await supabase
        .from("kyc_applications")
        .delete()
        .eq("id", applicationId);

      if (error) {
        return handleSupabaseError(error);
      }

      return { success: true };
    } catch (error) {
      return handleSupabaseError(error);
    }
  }
}
